import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      // Leitner: keep the Optis hole/ring options in the mobile quick add
      const optis = productDetails?.querySelector('.shopify-app-block:has(.bss-po-option-set)');
      if (optis && productFormComponent) productGrid.insertBefore(optis, productFormComponent);
      else if (optis) productGrid.appendChild(optis);

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#renderOptis(modalContent);
  }

  /**
   * Leitner: asks Optis (BSS Product Options) to draw its hole/ring options inside the quick-add modal.
   * Optis only renders by itself on the product page; its quick-view hooks below point it at the modal.
   * @param {Element} modalContent - The modal content element
   */
  #renderOptis(modalContent) {
    const set = modalContent.querySelector('.bss-po-option-set');
    const po = /** @type {any} */ (window).BSS_PO;
    if (!set || !po?.po?.generateOptionSetOnCollectionPage) return;
    set.querySelectorAll('[id^="po_container"] > *').forEach((el) => el.remove());
    const w = /** @type {any} */ (window);
    const productId = this.dataset.productId;
    w.bssFixSupportCustomQVInHomePage = () => !!document.querySelector('#quick-add-modal-content .bss-po-option-set');
    w.bssFixSupportGetAppliedOptionSetOnQVCollectionPage = () => productId;
    w.bssFixSupportGenerateOptionSetOnCollectionPage = (_t, html) => {
      const target = document.querySelector('#quick-add-modal-content .bss-po-option-set');
      target?.insertAdjacentHTML('afterbegin', html);
      // Optis only builds this map on the product page; without it the ring never shows after the hole
      const optionSet = po.po.appliedOptionSetForOneProduct;
      if (optionSet?.options) po.po.idDependencyMap = po.helper.mapIdsWithDependencies(optionSet.options);
    };
    po.po.generateOptionSetOnCollectionPage().catch(() => {});
    leitnerOptisCart.bind(modalContent, this.productPageUrl, productId);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/**
 * Leitner: sends the quick-add "Add to cart" through Optis, so the hole/ring choice and price come along.
 * Optis only hooks the product page's own button, so the modal button is intercepted here.
 */
const leitnerOptisCart = {
  /** @type {any} */
  active: null,

  /**
   * @param {Element} modalContent
   * @param {string} productPageUrl
   * @param {string | undefined} productId
   */
  bind(modalContent, productPageUrl, productId) {
    const el = /** @type {HTMLElement} */ (modalContent);
    el.dataset.lfProductUrl = productPageUrl;
    el.dataset.lfProductId = productId || '';
    this.installHooks();
    if (el.dataset.lfOptisBound) return;
    el.dataset.lfOptisBound = 'true';
    el.addEventListener('click', (event) => this.handleClick(event, el), true);
  },

  /**
   * @param {Event} event
   * @param {HTMLElement} modalContent
   */
  async handleClick(event, modalContent) {
    const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button') : null;
    if (!(button instanceof HTMLElement) || !modalContent.contains(button)) return;
    const po = /** @type {any} */ (window).BSS_PO;
    const optionSet = po?.po?.appliedOptionSetForOneProduct;
    if (!optionSet || !modalContent.querySelector('.bss-po-option-set [id^="po_container"] > *')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.active || button.hasAttribute('disabled')) return;

    const form = modalContent.querySelector('form[action*="/cart/add"]');
    const variantId = form?.querySelector('[name="id"]')?.getAttribute('value') || '';
    const quantityInput = /** @type {HTMLInputElement | null} */ (modalContent.querySelector('[name="quantity"]'));
    const quantity = Number(quantityInput?.value) || 1;

    let product;
    try {
      const url = new URL(modalContent.dataset.lfProductUrl || '', window.location.origin);
      url.search = '';
      product = await fetch(`${url.pathname.replace(/\/$/, '')}.js`).then((r) => r.json());
    } catch (error) {
      console.warn('[quick-add] Could not load product for Optis', error);
      return;
    }
    const variant = product.variants.find((/** @type {any} */ v) => String(v.id) === String(variantId)) || product.variants[0];

    this.active = { variant, quantity, button, productId: modalContent.dataset.lfProductId };
    po.po
      .addToCart({
        e: event,
        add_to_cart_btn: button,
        appliedOptionSet: optionSet,
        appliedOptionSetForSpecificVariant: null,
        variantProductIdApplyOptionSet: null,
        currentProduct: product,
        customCondition: false,
        dataHomePage: '',
        integration: false,
        numbericBtnAddToCart: 0,
      })
      .finally(() => {
        // Optis returns before its own fetch finishes; on a validation error nothing is added
        setTimeout(() => {
          if (this.active && !this.active.adding) this.active = null;
        }, 0);
      });
  },

  /**
   * Optis leaves out its "Upsell product" add-ons (the rings) when adding from the quick add:
   * no _bssVariants and no price, so the ring is neither charged nor put on the order.
   * Fills them in from the option set, the same way Optis does on the product page.
   * @param {any} body
   */
  addUpsellProducts(body) {
    const options = /** @type {any} */ (window).BSS_PO?.po?.appliedOptionSetForOneProduct?.options || [];
    const isString = typeof body === 'string';
    let data;
    try {
      data = isString ? JSON.parse(body) : body;
    } catch {
      return body;
    }
    const items = Array.isArray(data?.items) ? data.items : null;
    if (!items) return body;
    const euro = (/** @type {number} */ n) => '€' + n.toFixed(2).replace('.', ',');

    for (const item of items) {
      const props = item.properties;
      if (!props) continue;
      let variants = {};
      try {
        variants = JSON.parse(props._bssVariants || '{}');
      } catch {}
      let extra = 0;
      try {
        extra = Number(JSON.parse(props._bssPrice || '{}').extra) || 0;
      } catch {}
      let changed = false;

      for (const option of options) {
        const cartLabel = String(option.label_on_cart || option.label_on_product || '').trim();
        const key = Object.keys(props).find((k) => k.trim() === cartLabel);
        if (!key || variants[cartLabel]) continue;
        const value = String(props[key]);
        let label, id, price, qty;

        // Switch (e.g. Perforation): one upsell variant, value "Yes"
        const switchId = option.extra_price?.switch_extra_variant?.[0];
        if (switchId && option.extra_price.switch_extra_price_type?.[0] === 1) {
          label = value.replace(/\s*\(\+.*$/, '').trim();
          id = switchId;
          price = Number(option.extra_price.switch_extra_price?.[0]) || 0;
          qty = null;
        } else {
          // Swatch (e.g. ring colours): one upsell variant per swatch, value "1 x + Silver ring"
          const ids = option.extra_price?.swatch_extra_variant;
          const labels = option.swatch_label;
          if (!Array.isArray(ids) || !Array.isArray(labels)) continue;
          const index = labels.findIndex((/** @type {string} */ l, /** @type {number} */ i) => ids[i] && value.includes(l));
          if (index < 0) continue;
          label = labels[index];
          id = ids[index];
          price = Number(option.extra_price.swatch_extra_price?.[index]) || 0;
          qty = Number((value.match(/^\s*(\d+)\s*x/) || [])[1]) || 1;
        }

        variants[cartLabel] = { [label]: { id: String(id), quantity: qty } };
        if (!value.includes('(+')) props[key] = `${value} (+ ${euro(price * (qty || 1))})`;
        extra += price * (qty || 1);
        changed = true;
      }

      if (!changed) continue;
      extra = Math.round(extra * 100) / 100;
      props._bssVariants = JSON.stringify(variants);
      props._bssPrice = JSON.stringify({ extra });
      props.__bss_po_addons = String(extra);
      const custom = {};
      for (const [k, v] of Object.entries(props)) if (!k.startsWith('_bss')) custom[k] = v;
      props._bssCustomAttributes = JSON.stringify(custom);
    }
    return isString ? JSON.stringify(data) : data;
  },

  /** Optis checks for these global hooks; they only act while a quick-add is being added. */
  installHooks() {
    const w = /** @type {any} */ (window);
    if (w.__lfOptisHooks) return;
    w.__lfOptisHooks = true;

    const prevVariant = w.bssFixSupportFinalGetVariant;
    w.bssFixSupportFinalGetVariant = async (/** @type {any[]} */ ...args) => {
      if (this.active) return this.active.variant;
      return prevVariant ? prevVariant(...args) : args[1];
    };

    const prevQuantity = w.bssFixSupportGetDefaultQuantity;
    w.bssFixSupportGetDefaultQuantity = (/** @type {any[]} */ ...args) => {
      if (this.active) return { value: this.active.quantity };
      return prevQuantity ? prevQuantity(...args) : args[1];
    };

    const prevBody = w.bssFixSupportUpdateFormatBody;
    w.bssFixSupportUpdateFormatBody = (/** @type {any} */ t, /** @type {any} */ body) => {
      if (this.active) {
        this.active.adding = true;
        body = this.addUpsellProducts(body);
      }
      return prevBody ? prevBody(t, body) : body;
    };

    const prevAfter = w.bssFixSupportCustomAfterAddToCart;
    w.bssFixSupportCustomAfterAddToCart = (/** @type {any} */ t, /** @type {any} */ response, /** @type {any} */ btn, /** @type {any} */ loading) => {
      const active = this.active;
      if (!active || btn !== active.button) {
        if (prevAfter) return prevAfter(t, response, btn, loading);
        if (t.enableMiniCart) {
          t.cart.handleMiniCart(response);
          t.helper.removeLoadingBtnCSS(btn, loading);
        } else {
          window.location.pathname = window.Shopify.routes.root + 'cart';
        }
        return;
      }
      this.active = null;
      t.helper.removeLoadingBtnCSS(btn, loading);
      this.announce(active);
    };
  },

  /**
   * Tells Horizon (cart drawer, cart count, quick-add modal) that a line was added.
   * @param {any} active
   */
  async announce(active) {
    const deferred = CartLinesUpdateEvent.createPromise();
    active.button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(active.variant.id), quantity: active.quantity }],
        promise: deferred.promise,
      })
    );
    try {
      const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
      const cart = cartItems?.fetchCartData
        ? await cartItems.fetchCartData()
        : await fetch(`${Theme.routes.cart_url}.json`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: 'leitner-quick-add',
          itemCount: active.quantity,
          productId: active.productId,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
    }
  },
};

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
